import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, LogOut, Eye, Trash2, Phone, Scissors, Package, MapPin, Info, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  consulta: { label: 'Consulta', icon: MessageSquare, color: 'bg-blue-100 text-blue-800' },
  cita_telefonica: { label: 'Cita Telefonica', icon: Phone, color: 'bg-purple-100 text-purple-800' },
  problema_pedido: { label: 'Problema Pedido', icon: Package, color: 'bg-red-100 text-red-800' },
  protesis_semipersonalizada: { label: 'Protesis Semi.', icon: Scissors, color: 'bg-amber-100 text-amber-800' },
  info_general: { label: 'Info General', icon: Info, color: 'bg-gray-100 text-gray-800' },
  cita_fisica_madrid: { label: 'Cita Madrid', icon: MapPin, color: 'bg-green-100 text-green-800' }
};

const STATUS_CONFIG = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  resuelto: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800'
};

export default function AdminContactRequests({ user, setUser }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); return; }
    fetchRequests();
  }, [user, navigate]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/contact-requests/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      toast.success('Estado actualizado');
      fetchRequests();
      if (selectedRequest?.id === id) setSelectedRequest(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/contact-requests/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: notes })
      });
      toast.success('Notas guardadas');
    } catch (error) {
      toast.error('Error al guardar notas');
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/contact-requests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Solicitud eliminada');
      if (selectedRequest?.id === id) setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); navigate('/'); };

  const filtered = requests.filter(r => {
    if (filterType !== 'all' && r.request_type !== filterType) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const stats = {
    total: requests.length,
    pendiente: requests.filter(r => r.status === 'pendiente').length,
    en_proceso: requests.filter(r => r.status === 'en_proceso').length,
    resuelto: requests.filter(r => r.status === 'resuelto').length
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-contact-requests">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 overflow-x-auto">
            <Link to="/admin" className="text-gray-600 hover:text-black whitespace-nowrap">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black whitespace-nowrap">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black whitespace-nowrap">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black whitespace-nowrap">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black whitespace-nowrap">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black whitespace-nowrap">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black whitespace-nowrap">Envios</Link>
            <Link to="/admin/contact-requests" className="font-medium text-black border-b-2 border-black pb-2 whitespace-nowrap">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black whitespace-nowrap">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black whitespace-nowrap">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black whitespace-nowrap">Galeria</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Solicitudes de Contacto</h2>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white p-4 border-2 border-black">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white p-4 border-2 border-yellow-400">
            <p className="text-sm text-gray-600">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pendiente}</p>
          </div>
          <div className="bg-white p-4 border-2 border-blue-400">
            <p className="text-sm text-gray-600">En Proceso</p>
            <p className="text-2xl font-bold text-blue-600">{stats.en_proceso}</p>
          </div>
          <div className="bg-white p-4 border-2 border-green-400">
            <p className="text-sm text-gray-600">Resueltos</p>
            <p className="text-2xl font-bold text-green-600">{stats.resuelto}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <select data-testid="filter-type" className="border-2 border-gray-300 rounded p-2 text-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">Todos los tipos</option>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select data-testid="filter-status" className="border-2 border-gray-300 rounded p-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="resuelto">Resuelto</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2 bg-white border-2 border-black">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left p-3 text-sm">Tipo</th>
                    <th className="text-left p-3 text-sm">Cliente</th>
                    <th className="text-left p-3 text-sm">Fecha</th>
                    <th className="text-left p-3 text-sm">Estado</th>
                    <th className="text-right p-3 text-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No hay solicitudes
                    </td></tr>
                  ) : filtered.map((req) => {
                    const typeInfo = TYPE_CONFIG[req.request_type] || TYPE_CONFIG.consulta;
                    return (
                      <tr key={req.id} className={`border-b hover:bg-gray-50 cursor-pointer ${selectedRequest?.id === req.id ? 'bg-gray-100' : ''}`}
                        onClick={() => setSelectedRequest(req)} data-testid={`request-row-${req.id}`}>
                        <td className="p-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${typeInfo.color}`}>{typeInfo.label}</span>
                        </td>
                        <td className="p-3">
                          <p className="font-medium text-sm">{req.name}</p>
                          <p className="text-xs text-gray-500">{req.phone}</p>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {new Date(req.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="p-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${STATUS_CONFIG[req.status] || 'bg-gray-100'}`}>
                            {req.status}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteRequest(req.id); }} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail Panel */}
          <div className={selectedRequest ? "fixed inset-0 z-50 bg-black/60 overflow-y-auto p-3 lg:static lg:bg-transparent lg:p-0 lg:z-auto lg:overflow-visible lg:col-span-1" : "lg:col-span-1"}>
            {selectedRequest ? (
              <div className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto" data-testid="admin-request-detail-wrapper">
                <div className="lg:hidden flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => setSelectedRequest(null)}
                    className="bg-white text-3xl leading-none px-3 py-1 rounded shadow border-2 border-black"
                    aria-label="Cerrar"
                    data-testid="admin-request-detail-close-mobile"
                  >
                    ×
                  </button>
                </div>
                <RequestDetail
                  request={selectedRequest}
                  onStatusChange={updateStatus}
                  onSaveNotes={saveNotes}
                />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Selecciona una solicitud para ver los detalles</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestDetail({ request, onStatusChange, onSaveNotes }) {
  const [notes, setNotes] = useState(request.admin_notes || '');
  const typeInfo = TYPE_CONFIG[request.request_type] || TYPE_CONFIG.consulta;

  useEffect(() => {
    setNotes(request.admin_notes || '');
  }, [request.id, request.admin_notes]);

  return (
    <div className="border-2 border-black bg-white" data-testid="request-detail">
      <div className="p-4 border-b bg-gray-50">
        <span className={`text-xs font-medium px-2 py-1 rounded ${typeInfo.color}`}>{typeInfo.label}</span>
        <h3 className="font-bold text-lg mt-2">{request.name}</h3>
        <p className="text-sm text-gray-500">{new Date(request.created_at).toLocaleString('es-ES')}</p>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-xs text-gray-500 uppercase">Contacto</p>
          <p className="text-sm">{request.email}</p>
          <p className="text-sm">{request.phone}</p>
        </div>

        {request.message && (
          <div>
            <p className="text-xs text-gray-500 uppercase">Mensaje</p>
            <p className="text-sm bg-gray-50 p-2 rounded">{request.message}</p>
          </div>
        )}

        {request.request_type === 'cita_telefonica' && (
          <div className="bg-purple-50 p-3 rounded text-sm space-y-1">
            <p className="font-medium text-purple-800">Preferencias de contacto</p>
            {request.preferred_days?.length > 0 && <p>Dias: {request.preferred_days.join(', ')}</p>}
            {request.preferred_time_slot && <p>Horario: {request.preferred_time_slot}</p>}
          </div>
        )}

        {request.request_type === 'problema_pedido' && request.order_reference && (
          <div className="bg-red-50 p-3 rounded text-sm">
            <p className="font-medium text-red-800">Ref. Pedido: {request.order_reference}</p>
          </div>
        )}

        {request.request_type === 'protesis_semipersonalizada' && (
          <div className="bg-amber-50 p-3 rounded text-sm space-y-1">
            <p className="font-medium text-amber-800">Caracteristicas pieza</p>
            {request.hair_color && <p>Color: {request.hair_color}</p>}
            {request.hair_density && <p>Densidad: {request.hair_density}</p>}
            {request.hair_texture && <p>Textura: {request.hair_texture}</p>}
            {request.base_type && <p>Tipo base: {request.base_type}</p>}
            {request.base_size && <p>Tamano base: {request.base_size}</p>}
            {request.grey_percentage && <p>Canas: {request.grey_percentage}</p>}
            {request.additional_specs && <p>Extra: {request.additional_specs}</p>}
          </div>
        )}

        {request.request_type === 'cita_fisica_madrid' && request.preferred_date && (
          <div className="bg-green-50 p-3 rounded text-sm">
            <p className="font-medium text-green-800">Fecha preferida: {request.preferred_date}</p>
          </div>
        )}

        <div>
          <p className="text-xs text-gray-500 uppercase mb-1">Estado</p>
          <select
            data-testid="status-select"
            className="w-full border-2 border-gray-300 rounded p-2 text-sm"
            value={request.status}
            onChange={(e) => onStatusChange(request.id, e.target.value)}
          >
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="resuelto">Resuelto</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        <div>
          <p className="text-xs text-gray-500 uppercase mb-1">Notas internas</p>
          <textarea
            data-testid="admin-notes"
            className="w-full border p-2 rounded text-sm"
            rows="3"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas para el equipo..."
          />
          <Button
            data-testid="save-notes-btn"
            size="sm"
            className="mt-1 bg-black text-white"
            onClick={() => onSaveNotes(request.id, notes)}
          >
            Guardar notas
          </Button>
        </div>
      </div>
    </div>
  );
}
